'use client';

import useSWR from 'swr';
import { useMemo, createContext, useContext } from 'react';

import { useAuthContext } from 'src/auth/hooks';
import { endpoints, fetcher } from 'src/utils/axios';
import type { DetectCountryApiResponse, UserGeoFromIpState } from 'src/utils/user-geo-from-ip-state';
import { resolveUserGeoFromIpState } from 'src/utils/user-geo-from-ip-state';

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

const defaultState: UserGeoFromIpState = {
  countryCode: undefined,
  isChina: false,
  isLoading: false,
  hideGoogleMaps: false,
  error: null,
};

const UserGeoFromIpContext = createContext<UserGeoFromIpState>(defaultState);

export function UserGeoFromIpProvider({ children }: { children: React.ReactNode }) {
  const { authenticated } = useAuthContext();
  const swrKey = authenticated ? endpoints.geocoding.detectCountry : null;

  const { data, isLoading, error } = useSWR<DetectCountryApiResponse>(
    swrKey,
    fetcher,
    swrOptions
  );

  const value = useMemo(
    () =>
      resolveUserGeoFromIpState({
        isAuthenticated: authenticated,
        isLoading,
        data,
        error,
      }),
    [authenticated, isLoading, data, error]
  );

  return <UserGeoFromIpContext.Provider value={value}>{children}</UserGeoFromIpContext.Provider>;
}

export function useUserGeoFromIp(): UserGeoFromIpState {
  return useContext(UserGeoFromIpContext);
}

export function useShouldHideGoogleMaps(): Pick<UserGeoFromIpState, 'hideGoogleMaps' | 'isLoading'> {
  const { hideGoogleMaps, isLoading } = useUserGeoFromIp();
  return { hideGoogleMaps, isLoading };
}
